import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { makePostRequest } from '../../services/httpHelper';
import ErrorHandler from '../../services/ErrorHandler';
import TopNavbar from '../Nav/TopNavbar';
import Footer from './Footer';

/* -------------------- component -------------------- */
export default function CheckoutForm() {
  const query   = new URLSearchParams(window.location.search);
  const product = query.get('edition');
  const plan    = query.get('plan');

  /* ───── form state ───── */
  const [form, setForm] = useState({
    firstName : '',
    lastName  : '',
    email     : '',
    phone     : '',
    company   : '',
    address   : '',
    countryId : '',
  });

  const [countries, setCountries] = useState([]);      // { id,label }
  const [loading  , setLoading  ] = useState(false);
  const [error    , setError    ] = useState('');

  const navigate = useNavigate();

  /* ───── fetch countries on mount ───── */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          'https://appstore-api.roshtech.co.ke/api/countries'
        );
        setCountries(
          data.map((c) => ({ id: c.id, label: c.country }))
        );
      } catch (err) {
        console.error('Country fetch error:', err);
      }
    })();
  }, []);

  /* ───── helpers ───── */
  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const isEmail = (val) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

  const hasEmptyRequired = () =>
    !form.firstName.trim() ||
    !form.lastName.trim() ||
    !form.email.trim() ||
    !form.phone.trim() ||
    !form.company.trim() ||
    !form.countryId;

  /* ───── submit ───── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (hasEmptyRequired()) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!isEmail(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const res = await makePostRequest('customer', {
        ...form,
        product,
        plan,
      });

      if (res?.errorType) {
        setError(ErrorHandler(res));
      } else if (res?.data?.order) {
        console.log('Order',res.data)
        navigate('/payment', { state: { order: res.data.order } });
      }
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- render -------------------- */
  return (
    <>
      <TopNavbar />
      <FormWrapper>
        <h2>
          Checkout for {product} – {plan}
        </h2>
        <p>We’ll deliver the software to your e‑mail.</p>

        {error && <p className="error">{error}</p>}

        <form onSubmit={handleSubmit} noValidate>
          <Inline>
            <input
              name="firstName"
              placeholder="First Name *"
              value={form.firstName}
              onChange={handleChange}
              required
            />
            <input
              name="lastName"
              placeholder="Last Name *"
              value={form.lastName}
              onChange={handleChange}
              required
            />
          </Inline>

          <input
            name="email"
            type="email"
            placeholder="Email Address *"
            value={form.email}
            onChange={handleChange}
            required
            pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
          />
          <input
            name="phone"
            placeholder="Phone Number *"
            value={form.phone}
            onChange={handleChange}
            required
          />
          <input
            name="company"
            placeholder="Company / Organisation *"
            value={form.company}
            onChange={handleChange}
            required
          />

          <select
            name="countryId"
            value={form.countryId}
            onChange={handleChange}
            required
          >
            <option value="">Select Country *</option>
            {countries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>

          <input
            name="address"
            placeholder="Address (optional)"
            value={form.address}
            onChange={handleChange}
          />

          <button type="submit" disabled={loading}>
            {loading ? 'Processing…' : 'Continue'}
          </button>
        </form>
      </FormWrapper>
      <Footer />
    </>
  );
}

/* -------------------- styled-components -------------------- */
const FormWrapper = styled.div`
  max-width: 620px;
  margin: 60px auto;
  padding: 28px 32px;
  border: 1px solid #ddd;
  border-radius: 12px;
  background: #fff;

  h2 { text-align: center; }

  .error   { color: #d33; margin-bottom: 14px; }

  form {
    display: flex;
    flex-direction: column;
    gap: 18px;
    margin-top: 22px;
  }

  input, select {
    padding: 12px;
    border: 1px solid #bbb;
    border-radius: 6px;
    font-size: 1rem;
    background: #fafafa;
  }

  select { background: #fff; }

  button {
    background: #6c2bd9;
    color: #fff;
    border: none;
    padding: 12px;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity .25s;
  }
  button:disabled { opacity:.55; cursor:not-allowed; }
`;

/* Two inputs side‑by‑side on desktop, stacked on mobile */
const Inline = styled.div`
  display: flex;
  gap: 12px;
  @media (max-width: 520px) {
    flex-direction: column;
  }
`;
